import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';
import { Box, Title, Text, Link } from '@nerdwallet/currency';

import {
  stringInputSpec,
  stringTextAreaSpec,
} from '../../../../lib/editing-configs';
import parseStyleFormatting from '../parse-awards-2023-formatting';
import styles from './partner-page.module.less';
import { useScrollAnimation } from '../useScrollAnimation';

const PartnerPage = ({
  title,
  subtitle,
  brandLogoUrl,
  brandLogoLink,
  topLeftIconUrl,
  topRightIconUrl,
  topLeftIconUrlMobile,
  bottomLeftIconUrl,
}) => {
  const topLeftStarRef = useRef();
  const bottomLeftStarRef = useRef();
  const bottomLeftStarMobileRef = useRef();
  const topRightStarRef = useRef();

  useScrollAnimation([
    { elementRef: topLeftStarRef, speed: 5 },
    { elementRef: bottomLeftStarRef, speed: 2 },
    { elementRef: bottomLeftStarMobileRef, speed: 2 },
    { elementRef: topRightStarRef, speed: 5, animationType: 'bounce' },
  ]);

  const leftStars = ({ bigStarColor }) => {
    return (
      <React.Fragment>
        {
          <Box>
            <img
              ref={
                bigStarColor === 'green'
                  ? bottomLeftStarMobileRef
                  : topLeftStarRef
              }
              src={
                bigStarColor === 'green' ? topLeftIconUrlMobile : topLeftIconUrl
              }
              alt="big star"
            />
          </Box>
        }

        {bottomLeftIconUrl && (
          <Box className={classNames([nds('text-align-right')])}>
            <img
              ref={bottomLeftStarRef}
              src={bottomLeftIconUrl}
              alt="pink star"
            />
          </Box>
        )}
      </React.Fragment>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.awardsHero2023BrandLogo}>
        {topLeftIconUrlMobile && (
          <Box className={styles.awardsHero2023LeftStarsMobile}>
            {leftStars({ bigStarColor: 'green' })}
          </Box>
        )}
        {brandLogoUrl && (
          <Box className={styles.awardsHero2023LogoWrapper}>
            {brandLogoLink ? (
              <Link href={brandLogoLink}>
                <img src={brandLogoUrl} alt="brand logo" />
              </Link>
            ) : (
              <img src={brandLogoUrl} alt="brand logo" />
            )}
          </Box>
        )}
      </Box>

      <Box className={styles.awardsHero2023Main}>
        {topLeftIconUrl && (
          <Box className={styles.awardsHero2023LeftStarsLarge}>
            {leftStars({ bigStarColor: 'yellow' })}
          </Box>
        )}

        <Box className={styles.awardsHero2023TextContainer}>
          <Box
            display="flex"
            flexDirection="column"
            className={classNames([nds('margin-vertical-3')])}
          >
            <Title className={styles.awardsHero2023TextTitle}>
              {parseStyleFormatting(title)}
            </Title>
            <Box className={styles.awardsHero2023TextSubtitle}>
              <Text component="p">{subtitle}</Text>
            </Box>
          </Box>
          {topRightIconUrl && (
            <Box className={styles.awardsHero2023RightStars}>
              <img
                ref={topRightStarRef}
                src={topRightIconUrl}
                alt="pink star"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

PartnerPage.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brandLogoLink: PropTypes.string,
  topLeftIconUrl: PropTypes.string,
  bottomLeftIconUrl: PropTypes.string,
  topRightIconUrl: PropTypes.string,
  topLeftIconUrlMobile: PropTypes.string,
};

PartnerPage.defaultProps = {
  brandLogoUrl: '',
  brandLogoLink: '',
};

PartnerPage.getEditingConfig = () => {
  return [
    stringTextAreaSpec('title', 'Title', {
      help: 'For Gotham black font surround with a | like |this|, For italics, surround title text with ^ like ^this^. To include a linebreak, insert :br: in the textbox',
    }),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('brandLogoUrl', 'Brand Logo URL'),
    stringInputSpec('brandLogoLink', 'Brand Logo Link'),
    stringInputSpec('topLeftIconUrl', 'Top Left Image URL'),
    stringInputSpec('topLeftIconUrlMobile', 'Top Left Image URL Mobile'),
    stringInputSpec('bottomLeftIconUrl', 'Bottom Left Image URL'),
    stringInputSpec('topRightIconUrl', 'Top Right Image URL'),
  ];
};

export default PartnerPage;
